/**
 * Aspect — Client Bootstrap (The Body)
 * --------------------------------------------------------------
 * Owns the PlayCanvas application lifecycle: graphics device creation, the
 * engine app, canvas sizing, context-loss recovery, and clean teardown under
 * Vite HMR (otherwise each hot reload would stack a new WebGL context and leak).
 *
 * Day 2 scope: prove the render pipeline with a spinning placeholder cube.
 * Later days plug networking (Day 3), environment/lighting (Day 8), the scene
 * manager (Day 9-10), and interaction (Day 11) into this same app instance.
 */

import * as pc from "playcanvas";
import "./app.css";
import { NetworkManager } from "./network.js";
import { SceneManager } from "./scene_manager.js";
import { setupRendering, addLights, createCamera, setupHdriSky, setupPostProcessing, detectPerfTier } from "./scene/environment.js";
import { breakOutOfIframeOnMobile, isTouchUi, isAndroid, isAppleMobile, noteSafariBoot, mobileGraphicsTier } from "./device_profile.js";
import { DayNightCycle, DEFAULT_INITIAL_HOUR } from "./scene/day_night_cycle.js";
import { BakedLighting } from "./scene/baked/baked_lighting.js";
import { LutGrading } from "./scene/baked/lut_grading.js";
import { Picker } from "./interaction/picker.js";
import { GizmoControls } from "./interaction/gizmo_controls.js";
import { PlacementTool } from "./interaction/placement_tool.js";
import { FirstPersonControls } from "./interaction/first_person_controls.js";
import { SceneViewControls } from "./interaction/scene_view_controls.js";
import { StructureSelect } from "./interaction/structure_select.js";
import { WallCutaway } from "./scene/wall_cutaway.js";
import { DoorSystem } from "./interaction/door_system.js";
import { DecorSystem } from "./scene/decor.js";
import { LightSystem } from "./interaction/light_system.js";
import { LookInteraction } from "./interaction/look_interaction.js";
import { TourCamera } from "./interaction/tour_camera.js";
import { CinematicReel } from "./interaction/cinematic_reel.js";
import { MeasureTool } from "./interaction/measure_tool.js";
import { MarqueeSelect } from "./interaction/marquee_select.js";
import { SitSystem } from "./interaction/sit_system.js";
import { DistanceCulling } from "./scene/distance_culling.js";
import { ReviveMode } from "./interaction/revive_mode.js";
import { ReviveOverlay } from "./ui/revive_overlay.js";
import { ReviveGallery } from "./ui/revive_gallery.js";
import { requestReviveRender } from "./revive_render.js";
import { TourPanel } from "./ui/tour_panel.js";
import { TouchJoystickUI } from "./ui/touch_joystick.js";
import { Overlay } from "./ui/overlay.js";
import { Minimap } from "./ui/minimap.js";
import { PlanEditor } from "./ui/plan_editor.js";
import { rebuildWallSubset, removeWalls, refreshEnvelope, stripStructure } from "./scene/room_geometry.js";
import { initOpeningAssets } from "./scene/opening_assets.js";
import { AgentClient } from "./agent_client.js";
import { PlanAnalyzer, fetchActivePlan, fetchViewportState, fetchTour } from "./plan_analyzer.js";
import { saveSavedPlan, clearSavedPlan } from "./plan_store.js";
import { loadSessionTour } from "./session_tour_store.js";

// Mark touch devices ASAP so CSS can strip the desktop HUD before first paint.
if (typeof window !== "undefined" && isTouchUi()) {
  document.body.classList.add("touch-ui");
}

// Build beacon: shows in DevTools console — if a bug report doesn't match the
// current tag, the tab is running a stale bundle and needs a hard refresh.
const CLIENT_BUILD = "2026-09-14.8 carpet C only";
console.info(`[aspect] client build: ${CLIENT_BUILD}`);

const CANVAS_ID = "app-canvas";
// Cap retina scaling: rendering at full DPR on 3x phones murders fill rate.
const MAX_PIXEL_RATIO = 2;
const BOOT_PARAMS = new URLSearchParams(window.location.search);
const DEBUG = BOOT_PARAMS.has("debug");
// Read-only presentation modes: "?tour=<id>" boots from a published snapshot
// with all editing disabled; "?embed=1" additionally strips the HUD to a
// minimal iframe-friendly chrome (walk + auto-tour + labels only).
const TOUR_ID = BOOT_PARAMS.get("tour");
const EMBED = BOOT_PARAMS.get("embed") === "1" || BOOT_PARAMS.get("embed") === "true";
const READ_ONLY = Boolean(TOUR_ID) || EMBED;
// Production SampleTour: session 2D rebuilds never write the shared default.
// Published `?tour=` links stay read-only; iframe embeds still sandbox.
const SANDBOX = import.meta.env.PROD && !TOUR_ID;
// Interactive web demo: catalog + move/rotate/delete + day/night + cost +
// walk/plan/overhead + flooring — but no AI prompt or 2D plan upload.
const DEMO =
  BOOT_PARAMS.get("demo") === "1" ||
  BOOT_PARAMS.get("demo") === "true" ||
  String(import.meta.env.VITE_DEMO ?? "") === "true";
// Launch-film capture: scripted camera + live features. Record the canvas.
const REEL =
  BOOT_PARAMS.get("reel") === "1" ||
  BOOT_PARAMS.get("reel") === "true";

// Desktop keeps detectPerfTier(). Phones use the "low" light/shadow maps plus
// a Safari-tuned GPU preset (pixel ratio 2, no MSAA, 2K atlas) so iOS can
// still push the GPU without the tab being killed.
const IS_TOUCH = document.body.classList.contains("touch-ui") || isTouchUi();
const PERF_TIER = IS_TOUCH ? "low" : detectPerfTier();
if (IS_TOUCH) noteSafariBoot();
const GFX = IS_TOUCH ? mobileGraphicsTier() : "desktop";
console.info(`[aspect] graphics: ${GFX}`, {
  android: isAndroid(),
  apple: isAppleMobile(),
  bedroom: IS_TOUCH,
  sandbox: SANDBOX,
});

// Keep https://aspect.ge/SampleTour in the address bar on phones (do not
// replace the parent tab with aspect-staging.fly.dev).
if (!EMBED) breakOutOfIframeOnMobile();

if (IS_TOUCH) {
  window.addEventListener("unhandledrejection", (e) => {
    console.warn("[aspect] unhandled", e.reason);
    e.preventDefault();
  });
}

async function createApp(canvas) {
  const deviceOpts = {
    // Strings: PlayCanvas 2 does not export DEVICETYPE_WEBGL1; Android needs the
    // WebGL1 fallback when WebGL2 context creation fails.
    deviceTypes: IS_TOUCH ? ["webgl2", "webgl1"] : ["webgl2"],
    antialias: !IS_TOUCH,
    alpha: false,
    powerPreference: IS_TOUCH ? "default" : "high-performance",
    failIfMajorPerformanceCaveat: false,
  };
  let device;
  try {
    device = await pc.createGraphicsDevice(canvas, deviceOpts);
  } catch (err) {
    console.warn("[aspect] WebGL2 device failed — retrying WebGL1", err);
    device = await pc.createGraphicsDevice(canvas, {
      ...deviceOpts,
      deviceTypes: ["webgl1"],
      antialias: false,
    });
  }
  const maxRatio = IS_TOUCH
    ? (GFX === "hq" ? 1.5 : GFX === "medium" ? 1.25 : 1)
    : PERF_TIER === "low" ? 1.25 : MAX_PIXEL_RATIO;
  device.maxPixelRatio = Math.min(window.devicePixelRatio || 1, maxRatio);

  const options = new pc.AppOptions();
  options.graphicsDevice = device;
  options.componentSystems = [
    pc.RenderComponentSystem,
    pc.CameraComponentSystem,
    pc.LightComponentSystem,
    pc.ScriptComponentSystem,
  ];
  options.resourceHandlers = [pc.TextureHandler, pc.ContainerHandler, pc.CubemapHandler];

  // Input devices (the sanctioned PlayCanvas way; not prohibited UI-DOM coupling).
  options.mouse = new pc.Mouse(canvas);
  options.touch = new pc.TouchDevice(canvas);
  options.keyboard = new pc.Keyboard(window);

  const app = new pc.AppBase(canvas);
  app.init(options);
  app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
  app.setCanvasResolution(pc.RESOLUTION_AUTO);
  return app;
}

const canvas = document.getElementById(CANVAS_ID);
if (!canvas) {
  showBootError(new Error("missing #app-canvas"));
  throw new Error("missing #app-canvas");
}

// Recover gracefully if the GPU process is lost (common on mobile/tab-switch).
canvas.addEventListener(
  "webglcontextlost",
  (e) => {
    e.preventDefault();
    console.warn("[aspect] WebGL context lost");
  },
  false,
);
canvas.addEventListener(
  "webglcontextrestored",
  () => {
    console.info("[aspect] WebGL context restored");
    const aspect = window.__aspect;
    // After a GPU kill, drop extra passes so Safari can keep the tab alive.
    if (IS_TOUCH && aspect) {
      try {
        aspect.postFx?.setQuality("off");
      } catch {
        /* ignore */
      }
      if (aspect.app?.graphicsDevice) aspect.app.graphicsDevice.maxPixelRatio = 1;
      aspect.app?.resizeCanvas?.();
    }
    // PlayCanvas rebuilds GPU resources on the next frame; nudge a render.
    if (aspect?.app) aspect.app.renderNextFrame = true;
  },
  false,
);
canvas.addEventListener("contextmenu", (e) => e.preventDefault());

function showBootError(err) {
  console.error("[aspect] boot failed", err);
  const intro = document.getElementById("intro-status-text");
  if (intro) intro.textContent = "Couldn't start the 3D tour. Reload and try again.";
  const card = document.querySelector(".intro-screen__card");
  if (card && !document.getElementById("boot-retry")) {
    const btn = document.createElement("button");
    btn.id = "boot-retry";
    btn.type = "button";
    btn.className = "intro-screen__retry";
    btn.textContent = "Try again";
    btn.addEventListener("click", () => location.reload());
    card.appendChild(btn);
  }
}

let app;
try {
  app = await createApp(canvas);
} catch (err) {
  showBootError(err);
  throw err;
}

// Keep the backbuffer in sync with the element size (handles rotation/resizes).
const resizeObserver = new ResizeObserver(() => app.resizeCanvas());
resizeObserver.observe(canvas);

// Allow phones to rotate freely (portrait + landscape). Some browsers keep a
// prior lock from fullscreen / PWA — unlock and reflow the canvas on change.
async function unlockOrientation() {
  try {
    await screen.orientation?.unlock?.();
  } catch {
    /* ignored — not all browsers expose unlock */
  }
}

function syncOrientationLayout() {
  const landscape =
    window.matchMedia("(orientation: landscape)").matches ||
    Math.abs(Number(screen.orientation?.angle ?? window.orientation ?? 0)) === 90;
  document.body.classList.toggle("orient-landscape", landscape);
  document.body.classList.toggle("orient-portrait", !landscape);
  app.resizeCanvas();
  app.renderNextFrame = true;
  app.fire("ui:orientation", {
    angle: screen.orientation?.angle ?? window.orientation ?? 0,
    type: screen.orientation?.type ?? (landscape ? "landscape" : "portrait"),
    landscape,
  });
}

unlockOrientation();
syncOrientationLayout();

const orientMq = window.matchMedia("(orientation: landscape)");
const onOrientMq = () => {
  unlockOrientation();
  // Wait a beat for the browser to finish rotating the visual viewport.
  window.setTimeout(syncOrientationLayout, 60);
  window.setTimeout(syncOrientationLayout, 220);
};
if (orientMq.addEventListener) orientMq.addEventListener("change", onOrientMq);
else orientMq.addListener?.(onOrientMq);

window.addEventListener("orientationchange", onOrientMq);
window.visualViewport?.addEventListener("resize", () => {
  app.resizeCanvas();
  app.renderNextFrame = true;
  // Some Android browsers only update orientation after visualViewport resize.
  const landscape = window.matchMedia("(orientation: landscape)").matches;
  const marked = document.body.classList.contains("orient-landscape");
  if (landscape !== marked) syncOrientationLayout();
});

// --- Environment: HDR sky + real-time sun/sky rotation from the time slider ---
setupRendering(app, { mobile: IS_TOUCH, gfx: GFX });
const lightingRoot = addLights(app, { tier: PERF_TIER, mobile: IS_TOUCH, gfx: GFX });
const camera = createCamera(app, { mobile: IS_TOUCH });
// Desktop: HDR before the first frame. Bedroom-only phones bake a medium
// env-atlas during the intro overlay (safe skipped after a crash loop).
if (!IS_TOUCH) {
  await setupHdriSky(app).catch((err) => console.error("[aspect] HDR sky failed:", err));
} else if (GFX !== "safe") {
  await setupHdriSky(app, { mobile: true, gfx: GFX }).catch((err) =>
    console.error("[aspect] HDR sky failed:", err),
  );
}

app.start();

// Desktop: SSAO / bloom / vignette. Bedroom HQ phones use the "mobile" stack
// (bloom + grade, no SSAO/TAA). Crash-loop stays on forward shading.
let postFx;
try {
  postFx = setupPostProcessing(app, camera, {
    quality: IS_TOUCH ? (GFX === "safe" ? "off" : "mobile") : PERF_TIER,
  });
} catch (err) {
  console.warn("[aspect] post-processing disabled", err);
  postFx = { quality: "off", frame: null, setQuality() {}, destroy() {} };
}
app.on("ui:render-quality", ({ quality }) => {
  if (IS_TOUCH) return;
  postFx.setQuality(quality);
});

if (IS_TOUCH) {
  const syncHidden = () => {
    if (document.hidden) {
      app.autoRender = false;
      app.timeScale = 0;
    } else {
      app.timeScale = 1;
      app.autoRender = true;
      app.renderNextFrame = true;
    }
  };
  document.addEventListener("visibilitychange", syncHidden);
}

const dayNight = new DayNightCycle(app, { lightingRoot, camera, initialHour: DEFAULT_INITIAL_HOUR });
app.on("ui:time-of-day", ({ hour }) => dayNight.setHour(hour, true));

if (DEBUG) {
  // Lightweight FPS/draw-call readout (a full perf overlay lands in Day 14).
  let acc = 0;
  let frames = 0;
  app.on("update", (dt) => {
    acc += dt;
    frames += 1;
    if (acc >= 1) {
      console.info(`[aspect] ${frames} fps | draws=${app.graphicsDevice.renderTarget ? "?" : app.stats.drawCalls.total}`);
      acc = 0;
      frames = 0;
    }
  });
}

// --- Networking: connect to the bridge as the "viewport" role ---
const net = new NetworkManager(app);

// Thin DOM binder lives in the shell (NOT inside any pc script) so the engine
// code stays DOM-decoupled. It only reflects net:status into the indicator.
const introScreen = document.getElementById("intro-screen");
const introStatusEl = document.getElementById("intro-status");
const introStatusText = document.getElementById("intro-status-text");
const introFillEl = document.getElementById("intro-progress-fill");
const introPctEl = document.getElementById("intro-progress-pct");
const statusEl = document.getElementById("net-status");
let introDismissed = false;
let introPct = 0;

const INTRO_STATUS = {
  connecting: { label: "Waking the engine…", tone: "loading", pct: 6 },
  reconnecting: { label: "Reconnecting the site crew…", tone: "warn" },
  ready: { label: "Pouring the foundation…", tone: "ready", pct: 16 },
  closed: { label: "Offline — the crew lost the line", tone: "err" },
};

const PILL_STATUS = {
  connecting: { label: "connecting…", tone: "" },
  reconnecting: { label: "reconnecting…", tone: "warn" },
  ready: { label: "● live", tone: "ready" },
  closed: { label: "offline", tone: "err" },
  tour: { label: "● tour", tone: "ready" },
};

/** Progress bar is monotonic — late-started loads never walk it backward. */
function setIntroProgress(pct) {
  introPct = Math.max(introPct, Math.min(100, Math.max(0, pct)));
  if (introFillEl) introFillEl.style.width = `${introPct.toFixed(1)}%`;
  if (introPctEl) introPctEl.textContent = `${Math.round(introPct)}%`;
}

function setIntroStage(text) {
  if (introStatusText) introStatusText.textContent = text;
}

function setIntroStatus(state, attempt = 0) {
  const cfg = INTRO_STATUS[state] ?? { label: state, tone: "loading" };
  const text = attempt > 0 && state !== "ready" ? `${cfg.label} (#${attempt})` : cfg.label;
  setIntroStage(text);
  if (introStatusEl) {
    const tone = cfg.tone === "warn" ? " intro-progress--warn" : cfg.tone === "err" ? " intro-progress--err" : "";
    introStatusEl.className = `intro-progress${tone}`;
  }
  if (cfg.pct != null) setIntroProgress(cfg.pct);
}

function setPillStatus(state, attempt = 0) {
  if (!statusEl) return;
  const cfg = PILL_STATUS[state] ?? { label: state, tone: "" };
  const text = attempt > 0 && state !== "ready" ? `${cfg.label} (#${attempt})` : cfg.label;
  statusEl.textContent = text;
  statusEl.className = `net-status-pill${cfg.tone ? ` net-status-pill--${cfg.tone}` : ""}`;
}

function dismissIntro() {
  if (introDismissed) return;
  introDismissed = true;
  setIntroProgress(100);
  introScreen?.classList.add("intro-screen--out");
  syncGlassCursor();
  app.fire("ui:intro-dismissed", {});
  window.setTimeout(() => {
    introScreen?.classList.add("hidden");
    statusEl?.classList.add("hidden");
  }, 650);
}

app.on("net:status", ({ state, attempt }) => {
  if (!introDismissed) setIntroStatus(state, attempt ?? 0);
  setPillStatus(state, attempt ?? 0);
  statusEl?.classList.add("hidden");
});

// Scene manager consumes routed commands, builds room geometry, and (Day 9+)
// instantiates pooled furniture.
const scene = new SceneManager(app, net, { camera, skyTexture: null, lightingRoot, sandbox: SANDBOX });
scene.readOnly = READ_ONLY;

// Hybrid baked-lighting pipeline: day/night lightmaps + SH probes + box-
// projected reflections when a bake manifest exists for the loaded map, and
// custom LUT color grading on the CameraFrame. Realtime lighting remains the
// automatic fallback for unbaked or live-edited rooms.
const bakedLighting = new BakedLighting(app, { scene, lightingRoot });
const lutGrading = new LutGrading(app, postFx);
// Re-announce the clock so the blend modules sync to the boot hour.
dayNight.setHour(dayNight.hour, true);

document.body.classList.toggle("tour-mode", READ_ONLY);
document.body.classList.toggle("embed-mode", EMBED);
document.body.classList.toggle("demo-mode", DEMO && !READ_ONLY);

// Selection/picking. Keep the highlight aligned when placements update.
const doors = new DoorSystem(app, camera, scene);
// Procedural decor (Unity curtains on dry-room windows).
const decor = new DecorSystem(app, scene);
// Prefetch Unity door/window/curtain GLBs; swap window panes once loaded.
const installOpenings = () => {
  initOpeningAssets(scene.registry).then(() => {
    if (!scene.roomRoot || !scene.room?.walls?.length) return;
    rebuildWallSubset(
      app,
      scene.room,
      scene.roomRoot,
      scene.room.walls.map((w) => w.id),
      { skyTexture: scene.skyTexture },
    );
  });
};
installOpenings();
// Ceiling lights + switches placed in the 2D planner (click/F a switch to toggle).
const lights = new LightSystem(app, camera, scene);
const picker = new Picker(app, camera, scene, net, doors, lights);
picker.readOnly = READ_ONLY;
if (document.body.classList.contains("touch-ui")) {
  picker.disableSelect = true;
  picker.deselect();
}
const placement = new PlacementTool(app, camera, scene, net, picker);
picker.placement = placement;
app.on("scene:placement-updated", () => picker.refresh());

// Manual drag/rotate/swap controls (streams transforms back to the server).
const gizmo = new GizmoControls(app, camera, scene, net, picker);

// Admin Save must use the viewport's live poses (WS sync can lag/drop).
app.on("ui:admin-save-collect", () => {
  try {
    gizmo.flushPending?.();
  } catch {
    /* ignore */
  }
  const placements = typeof scene.exportAdminPlacements === "function" ? scene.exportAdminPlacements() : [];
  app.fire("ui:admin-save-placements", placements);
});

// First-person walk: click canvas to capture mouse, WASD to move, Esc to release.
const fp = new FirstPersonControls(app, camera, scene, { gizmo, placement, canvas });
const look = new LookInteraction(app, camera, scene, doors, lights);

// Sit on chairs/sofas/toilets, lay on beds — E key (desktop) / Interact (touch).
const sitSystem = new SitSystem(app, camera, scene, fp, { gizmo, placement });
void sitSystem;

// Overhead / fly "scene view" camera (Unity-Editor style). Toggled from the UI
// or by pressing V. Right-drag to look, WASD (with RMB) to fly, wheel to zoom.
const sceneView = new SceneViewControls(app, camera, scene, { canvas, fp });

// Room-to-room camera glides + auto-tour, and the tape-measure tool (M).
const tourCam = new TourCamera(app, camera, scene, { fp, sceneView });
const measure = new MeasureTool(app, camera, scene);
picker.measure = measure;
// Free-cursor left-drag rubber-band: select multiple pieces, move as a group.
const marquee = new MarqueeSelect(app, camera, scene, picker, placement, measure);
void marquee;

// Track which room the player is standing in — the budget HUD shows that
// room's name + furniture total (whole-home totals in overhead view).
let roomTrackAcc = 0;
app.on("update", (dt) => {
  roomTrackAcc += dt ?? 0.016;
  if (roomTrackAcc < 0.25) return;
  roomTrackAcc = 0;
  if (sceneView.overheadActive) {
    scene.setCurrentRoom(null);
    return;
  }
  const p = camera.getPosition();
  scene.setCurrentRoom(scene.roomAt(p.x, p.z));
});
// Walk-mode streaming culling: rooms beyond sight lines are hidden (the room
// the player stands in is always fully visible); overhead/plan show everything.
const distanceCulling = new DistanceCulling(app, camera, scene);
void distanceCulling;

// Revive Mode: freeze the current view into an interactive 2D "AI render"
// (hover prices on flat pixels), then cross-fade back into the live tour.
// Engine state machine and DOM overlay are separate modules wired here.
const reviveOverlay = new ReviveOverlay({
  onEnterTour: () => reviveMode.restoreReviveState({ fadeMs: 500 }),
  onOpenGallery: () => reviveGallery.open(),
  onHover: ({ info, screen, follow } = {}) => {
    app.fire("scene:hover-info", { info: info ?? null, screen, follow });
  },
  onHitboxesChange: (state) => {
    const src = reviveOverlay.currentImage();
    if (src) reviveGallery.updateState(src, state);
  },
  onPhotoreal: () => {
    const src = reviveOverlay.currentImage();
    void enhanceReviveStill(src, {
      hour: reviveMode.capturedSnapshot()?.hour ?? dayNight.hour,
      onBusy: () => void reviveOverlay.setBusy(true),
      onDone: async (next) => {
        await reviveOverlay.setImage(next, { photoreal: true });
        reviveGallery.replaceSrc(src, next, { title: "Letting the light in", photoreal: true });
        await reviveOverlay.setBusy(false);
        reviveOverlay.setStatus("Hover items for live prices", "ok");
      },
      onError: (msg) => {
        void reviveOverlay.setBusy(false);
        reviveOverlay.setStatus(msg, "error");
      },
    });
  },
});
const reviveGallery = new ReviveGallery({
  onOpen: (item) => {
    void reviveMode.openSaved({ state: item.state, image: item.src, photoreal: item.photoreal });
  },
  onPhotoreal: (item, index) => {
    const previous = item.src;
    void enhanceReviveStill(previous, {
      hour: item.state?.hour ?? dayNight.hour,
      onBusy: () => reviveGallery.setBusy(true),
      onDone: async (next) => {
        const overlayWasThis = reviveOverlay.currentImage() === previous;
        reviveGallery.replaceSrc(index, next, { title: "Photo to Render", photoreal: true });
        if (overlayWasThis) await reviveOverlay.setImage(next, { photoreal: true });
        reviveGallery.setBusy(false);
      },
      onError: (msg) => {
        reviveGallery.setBusy(false, msg);
      },
    });
  },
});
const reviveMode = new ReviveMode(app, camera, scene, {
  fp,
  overlay: reviveOverlay,
  dayNight,
  sceneView,
});
app.on("ui:revive-gallery", () => reviveGallery.toggle());
app.on("revive:entered", () => {
  const src = reviveOverlay.currentImage();
  const state = reviveMode.capturedSnapshot();
  if (src && state) reviveGallery.add({ src, title: "Captured view", state });
});
async function enhanceReviveStill(src, { hour, onBusy, onDone, onError } = {}) {
  if (!src) {
    onError?.("Capture a Revive still first.");
    return null;
  }
  onBusy?.(true);
  try {
    const next = await requestReviveRender(src, { hour });
    await onDone?.(next);
    return next;
  } catch (err) {
    onError?.(err.message || "Photoreal render failed.");
    return null;
  }
}

void reviveMode;

// Overhead-only structure selection: left-drag marquee to pick walls/doors/windows.
const structureSelect = new StructureSelect(app, camera, scene, { canvas });
const wallCutaway = new WallCutaway(app, camera, scene, {
  enabled: () => sceneView.overheadActive,
});
wallCutaway.registerMaterial(doors.doorMat);
app.on("ui:toggle-camera", () => {
  if (document.body.classList.contains("touch-ui")) return;
  sceneView.toggle();
});
app.on("ui:set-view-mode", ({ mode }) => {
  // Phones stay in walk — no Walk/Overhead/Plan mode switch.
  if (document.body.classList.contains("touch-ui")) {
    if (sceneView.enabled || sceneView.isTransitioning) sceneView.disable();
    return;
  }
  if (mode === "overhead") {
    if (!sceneView.overheadActive) sceneView.enable();
    return;
  }
  if (mode === "plan") {
    if (sceneView.enabled || sceneView.isTransitioning) sceneView.disable();
    app.fire("ui:open-plan-editor", {});
    return;
  }
  // walk
  if (sceneView.enabled || sceneView.isTransitioning) sceneView.disable();
});
app.keyboard?.on(pc.EVENT_KEYDOWN, (e) => {
  if (e.key !== pc.KEY_V) return;
  if (document.body.classList.contains("touch-ui")) return;
  const el = document.activeElement;
  const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
  if (!typing) sceneView.toggle();
});

const crosshairEl = document.getElementById("crosshair");
const aspectCursorEl = document.getElementById("aspect-cursor");
const cursorState = {
  locked: false,
  inView: false,
  tx: 0,
  ty: 0,
  x: 0,
  y: 0,
};

function aspectFreeCursorAllowed() {
  // Revive is a 2D hover layer — always keep the (smaller) glass ring following the mouse.
  if (document.body.classList.contains("revive-open")) {
    return !document.body.classList.contains("touch-ui");
  }
  if (!introDismissed || cursorState.locked) return false;
  // Buy coming-soon modal owns the screen — keep the glass ring behind it (hidden).
  if (document.body.classList.contains("buy-soon-open")) return false;
  // Mobile: same glass ring as desktop, pinned to screen center (look aim point).
  if (document.body.classList.contains("touch-ui")) {
    return !document.body.classList.contains("plan-editor-open");
  }
  return true;
}

function syncGlassCursor() {
  const touchUi = document.body.classList.contains("touch-ui");
  if (touchUi && aspectFreeCursorAllowed()) {
    cursorState.x = window.innerWidth / 2;
    cursorState.y = window.innerHeight / 2;
    cursorState.tx = cursorState.x;
    cursorState.ty = cursorState.y;
    cursorState.inView = true;
  }

  const showLock = introDismissed && cursorState.locked && !touchUi;
  const showFree = aspectFreeCursorAllowed() && cursorState.inView;

  crosshairEl?.classList.toggle("hidden", !showLock);
  aspectCursorEl?.classList.toggle("hidden", !showFree);
  document.body.classList.toggle("aspect-cursor-active", showFree);
  crosshairEl?.setAttribute("aria-hidden", showLock ? "false" : "true");
  aspectCursorEl?.setAttribute("aria-hidden", showFree ? "false" : "true");

  if (showFree && aspectCursorEl) {
    aspectCursorEl.style.left = `${cursorState.x}px`;
    aspectCursorEl.style.top = `${cursorState.y}px`;
  }
}

document.documentElement.addEventListener("mouseenter", () => {
  cursorState.inView = true;
  syncGlassCursor();
});
document.documentElement.addEventListener("mouseleave", () => {
  cursorState.inView = false;
  syncGlassCursor();
});
document.addEventListener("mousemove", (e) => {
  cursorState.tx = e.clientX;
  cursorState.ty = e.clientY;
  cursorState.inView = true;
  if (aspectFreeCursorAllowed()) {
    if (aspectCursorEl?.classList.contains("hidden")) {
      cursorState.x = e.clientX;
      cursorState.y = e.clientY;
    }
    // Revive is 2D hover — pin the small ring to the pointer (no walk-mode lerp lag).
    if (document.body.classList.contains("revive-open") && aspectCursorEl) {
      cursorState.x = e.clientX;
      cursorState.y = e.clientY;
      aspectCursorEl.style.left = `${cursorState.x}px`;
      aspectCursorEl.style.top = `${cursorState.y}px`;
    }
  }
});

app.on("update", (dt) => {
  if (!aspectCursorEl || aspectCursorEl.classList.contains("hidden")) return;
  if (document.body.classList.contains("touch-ui")) {
    // Keep the ring locked to the look aim point (screen center).
    cursorState.tx = window.innerWidth / 2;
    cursorState.ty = window.innerHeight / 2;
  }
  const k = 1 - Math.exp(-32 * (dt ?? 0.016));
  cursorState.x += (cursorState.tx - cursorState.x) * k;
  cursorState.y += (cursorState.ty - cursorState.y) * k;
  aspectCursorEl.style.left = `${cursorState.x}px`;
  aspectCursorEl.style.top = `${cursorState.y}px`;
});

app.on("fp:lock-changed", ({ locked }) => {
  cursorState.locked = locked;
  if (!locked) crosshairEl?.classList.remove("glass-cursor--focus");
  syncGlassCursor();
});
app.on("look:target-changed", ({ active }) => {
  const focus = Boolean(active);
  crosshairEl?.classList.toggle("glass-cursor--focus", focus);
  aspectCursorEl?.classList.toggle("glass-cursor--focus", focus);
});
// The intro holds until the player's room is furnished (assets:stream leaves
// its "room" phase). Structure-only loads (no placements ever streamed) fall
// back to a timer so nobody gets stuck staring at the loading screen.
let introFallbackTimer = 0;
app.on("scene:room-loaded", () => {
  if (introDismissed) return;
  setIntroStage("Raising walls & ceilings…");
  setIntroProgress(26);
  window.clearTimeout(introFallbackTimer);
  introFallbackTimer = window.setTimeout(dismissIntro, 8000);
});
// Real GLB/texture streaming progress from the AssetRegistry (26% → 92%).
app.on("assets:progress", ({ done, total }) => {
  if (introDismissed || !total) return;
  setIntroProgress(26 + 66 * (done / total));
});
app.on("assets:stream", ({ phase }) => {
  if (introDismissed) return;
  if (phase === "room") {
    setIntroStage("Furnishing your rooms…");
    setIntroProgress(32);
    return;
  }
  // Player's room is ready — the rest of the home streams in behind gameplay.
  setIntroStage("Opening the front door…");
  window.clearTimeout(introFallbackTimer);
  window.setTimeout(dismissIntro, 350);
});
app.on("cam:mode-changed", ({ mode }) => {
  if (mode === "overhead") crosshairEl?.classList.remove("glass-cursor--focus");
  syncGlassCursor();
});
app.on("plan-editor:opened", () => syncGlassCursor());
app.on("plan-editor:closed", () => syncGlassCursor());
app.on("ui:buy-soon", () => syncGlassCursor());
app.on("revive:entered", () => {
  cursorState.inView = true;
  // Pointer-lock just released — snap the ring to the mouse so it isn't stuck at 0,0.
  if (!cursorState.x && !cursorState.y) {
    cursorState.x = cursorState.tx || window.innerWidth / 2;
    cursorState.y = cursorState.ty || window.innerHeight / 2;
  }
  syncGlassCursor();
});
app.on("revive:restored", () => {
  aspectCursorEl?.classList.remove("glass-cursor--focus");
  syncGlassCursor();
});

// Tailwind/HTML overlay (catalog, budget, selection, prompt) — DOM lives only here.
const overlayMount = document.getElementById("overlay");
const overlay = new Overlay(app, overlayMount, {
  demo: DEMO && !READ_ONLY,
  sandbox: SANDBOX,
  readOnly: READ_ONLY,
});
// Overlay is created after DayNightCycle; re-pin 11:00 so the desktop slider
// cannot keep a leftover 16:15 from an earlier time:changed.
dayNight.setHour(DEFAULT_INITIAL_HOUR, true);
const reel = new CinematicReel({ app, camera, scene, fp, sceneView, tourCam, dayNight, overlay });
if (REEL) {
  const bootReel = () => {
    if (!introDismissed) {
      requestAnimationFrame(bootReel);
      return;
    }
    void reel.start();
  };
  requestAnimationFrame(bootReel);
}
const minimap = new Minimap(app, camera, fp, overlayMount);
const tourPanel = new TourPanel(app, overlayMount, { tour: tourCam, measure, scene, camera, readOnly: READ_ONLY });
// Virtual joystick visual (touch walk) + GLB streaming progress chip.
const touchUi = new TouchJoystickUI(app, overlayMount);
const planAnalyzer = new PlanAnalyzer(app, { net, sandbox: SANDBOX });
const planEditor = new PlanEditor(app, overlayMount, {
  getPlanPreviewImage: () => overlay.getPlanImageDataUrl(),
  getBuiltPlanImage: () => minimap.exportPlanImageDataUrl({ maxPx: 1200 }),
  scene,
  net,
  placement,
  planAnalyzer,
  doors,
});

// Natural-language prompt -> HTTP orchestrator (Claude tool-use loop). Furniture
// changes stream back over the WS bridge; this only drives prompt/status.
const agentClient = DEMO || READ_ONLY ? null : new AgentClient(app, {
  getSnapshot: () => ({
    placements: scene.exportAdminPlacements(),
    room: scene.room ?? null,
    finishes: scene.room?.finishes ?? null,
  }),
});
if (agentClient) {
  app.on("ui:prompt", ({ prompt }) => agentClient.run(prompt));
}

// Attached 2D render -> HTTP orchestrator vision pass. The server rebuilds the
// room from the estimated dimensions and pushes it back over the WS bridge.
if (!DEMO && !READ_ONLY) {
  app.on("ui:image", ({ dataUrl }) => planAnalyzer.analyze(dataUrl));
  app.on("ui:rebuild-room", ({ spec }) => planAnalyzer.rebuild(spec));
  app.on("ui:refine-structure", (e) => planAnalyzer.refineStructure(e));
  app.on("ui:edit-structure", ({ graph }) => planAnalyzer.editStructure(graph));
  app.on("ui:recalibrate", (e) => planAnalyzer.recalibrate(e));
}

// Geometry edits route through the room_geometry pipeline; the wall cutaway
// refreshes itself via the "scene:geometry-changed" event it fires.
app.on("scene:openings-patched", ({ wallIds }) => {
  if (!scene.roomRoot || !scene.room || !wallIds?.length) return;
  rebuildWallSubset(app, scene.room, scene.roomRoot, wallIds, { skyTexture: scene.skyTexture });
});

app.on("scene:topology-patched", ({ wallIds, removeWallIds, removedOpeningIds }) => {
  if (!scene.roomRoot || !scene.room) return;
  const floorOnly = !(scene.room.walls?.length);
  if (floorOnly) {
    stripStructure(app, scene.roomRoot);
    doors.clearAll();
  } else {
    for (const id of removedOpeningIds ?? []) doors.removeOpening(id);
    if (removeWallIds?.length) removeWalls(app, scene.roomRoot, removeWallIds, removedOpeningIds);
    if (wallIds?.length) {
      rebuildWallSubset(app, scene.room, scene.roomRoot, wallIds, { skyTexture: scene.skyTexture });
    }
  }
  refreshEnvelope(app, scene.room, scene.roomRoot);
  scene.wallCollision?.rebuild(scene.room, doors);
});

app.on("ui:reset-home", () => planAnalyzer.resetHome());

app.on("ui:floor-change", ({ activeFloor, floorCount }) => {
  const prevFc = scene.room?.floorCount ?? 1;
  if (floorCount != null && floorCount !== prevFc) scene.setFloorCount(floorCount);
  else if (activeFloor != null) scene.setActiveFloor(activeFloor);
});

app.on("ui:home-reset", () => {
  picker.deselect();
  app.fire("fp:release-lock", {});
});

// Snapshot application is fully owned by SceneManager (queued + version-gated);
// the catalog rides inside the snapshot, so no separate pre-load is needed.
function applyViewportState(state) {
  if (!state?.room) return Promise.resolve();
  return scene.applySnapshot(state);
}

app.on("scene:http-resync", (state) => {
  void applyViewportState(state);
});

if (TOUR_ID) {
  // Published-tour boot: no live bridge — fetch the frozen snapshot and apply
  // it through the exact same pipeline as live sync. The client never writes.
  void (async () => {
    const record = await fetchTour(TOUR_ID);
    if (record?.snapshot) {
      await applyViewportState(record.snapshot);
      setPillStatus("tour");
      statusEl?.classList.remove("hidden");
    } else {
      setIntroStatus("closed");
      setIntroStage("Tour not found");
    }
  })();
} else {
  // After reconnect, sync the analysis panel and 3D scene with the orchestrator.
  app.on("net:ready", async () => {
    if (SANDBOX) {
      const session = loadSessionTour();
      if (session?.room) {
        scene.expectSandboxSnapshot();
        const viewport = await fetchViewportState();
        if (viewport?.catalog) scene.onCatalog(viewport.catalog);
        await applyViewportState({
          ...session,
          sandbox: true,
          catalog: viewport?.catalog ?? session.catalog,
        });
        return;
      }
    } else if (!DEMO) {
      const saved = await fetchActivePlan();
      if (saved?.spec) {
        saveSavedPlan(saved);
        app.fire("ai:analysis", { phase: "result", result: saved });
      } else {
        clearSavedPlan();
        app.fire("ui:home-reset", {});
      }
    } else {
      clearSavedPlan();
    }
    if (scene.isSessionTourActive()) return;
    const viewport = await fetchViewportState();
    if (viewport?.room) await applyViewportState(viewport);
  });

  net.connect();
}

// Full, idempotent teardown so repeated load/unload (HMR, navigation, soak
// tests) never stacks contexts or leaks GPU resources.
let destroyed = false;
function teardown() {
  if (destroyed) return;
  destroyed = true;
  resizeObserver.disconnect();
  bakedLighting?.destroy();
  lutGrading?.destroy();
  postFx?.destroy();
  dayNight?.destroy();
  picker.destroy();
  placement?.destroy();
  doors.destroy();
  decor.destroy();
  fp.destroy();
  sceneView.destroy();
  structureSelect.destroy();
  tourCam.destroy();
  reel?.stop?.();
  measure.destroy();
  tourPanel.destroy();
  touchUi.destroy();
  planEditor.destroy();
  scene.pool.destroy();
  scene.registry.destroy();
  net.destroy();
  app.destroy();
  delete window.__aspect;
}

window.addEventListener("beforeunload", teardown);

// Expose for later modules / debugging without re-querying the DOM.
window.__aspect = { app, net, scene, picker, gizmo, fp, sceneView, structureSelect, planEditor, wallCutaway, doors, decor, lights, look, overlay, agentClient, planAnalyzer, camera, dayNight, postFx, bakedLighting, lutGrading, tourCam, measure, reel, teardown };

// --- Vite HMR teardown: destroy the old app before a reload builds a new one ---
if (import.meta.hot) {
  import.meta.hot.dispose(teardown);
}

export { app };
